import { Check, CircleDashed } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "../atoms/Badge";
import { KeyValue } from "../atoms/KeyValue";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";

export interface RespaServiceItemProps {
	code: string;
	name: string;
	/** Whether this settlement-service category has any recorded actions. */
	completed: boolean;
	eventCount: number;
	lastEventAt?: string | null;
	className?: string;
}

function RespaServiceItem({
	code,
	name,
	completed,
	eventCount,
	lastEventAt,
	className,
}: RespaServiceItemProps): ReactNode {
	const hasActions = completed;

	return (
		<div
			className={cn(
				"flex items-center gap-3 rounded-md border p-3 transition-colors",
				hasActions
					? "border-statusSuccess/30 bg-statusSuccess/5"
					: "border-border",
				className,
			)}
		>
			<span
				aria-label={hasActions ? "Has recorded actions" : "No actions recorded"}
				className={cn(
					"flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
					hasActions
						? "bg-statusSuccess text-white"
						: "bg-muted text-mutedForeground",
				)}
			>
				{hasActions ? (
					<Check className="h-3 w-3" aria-hidden="true" />
				) : (
					<CircleDashed className="h-3 w-3" aria-hidden="true" />
				)}
			</span>
			<div className="flex flex-1 flex-wrap items-center gap-3">
				<div className="flex items-center gap-2">
					<Badge variant="outline">{code}</Badge>
					<Text size="sm" weight="medium" as="span">
						{name}
					</Text>
				</div>
				<div className="ml-auto flex flex-wrap items-center gap-3">
					{hasActions ? (
						<>
							<KeyValue
								label="Actions"
								value={String(eventCount)}
								direction="horizontal"
							/>
							{lastEventAt && (
								<KeyValue
									label="Last action"
									value={lastEventAt}
									direction="horizontal"
								/>
							)}
						</>
					) : (
						<Text size="xs" textColor="mutedForeground" as="span">
							No actions recorded
						</Text>
					)}
				</div>
			</div>
		</div>
	);
}
RespaServiceItem.displayName = "RespaServiceItem";

export default RespaServiceItem;
export { RespaServiceItem };
